// Downloads Tag Search Component
// Search input with dropdown results for selecting tags

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DownloadsManager } from '../managers/downloads-manager';
import { useManager } from '../../../core';
import './DownloadsTagSearch.css';

export function DownloadsTagSearch({ onTagSelect, selectedTags = [], className = '' }) {
  const { manager } = useManager(DownloadsManager);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredTags, setFilteredTags] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showAllTags, setShowAllTags] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Fetch available tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        if (manager) {
          const tags = await manager.getTags();
          const tagObjects = tags.map(tag => ({
            value: tag,
            label: tag.charAt(0).toUpperCase() + tag.slice(1) // Capitalize first letter
          }));
          setAvailableTags(tagObjects);
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        setAvailableTags([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [manager]);

  // Get all available tags (excluding selected ones), sorted alphabetically, limited to 6
  const getAllTags = useCallback(() => {
    return availableTags
      .filter(tag => !selectedTags.includes(tag.value))
      .sort((a, b) => a.label.localeCompare(b.label))
      .slice(0, 6);
  }, [availableTags, selectedTags]);

  // Filter available tags based on search term
  const filterTags = useCallback((term) => {
    if (!term.trim()) {
      return [];
    }

    const filtered = availableTags.filter(tag =>
      tag.label.toLowerCase().includes(term.toLowerCase()) &&
      !selectedTags.includes(tag.value)
    );

    return filtered.slice(0, 8); // Limit to 8 results
  }, [availableTags, selectedTags]);

  // Update filtered results when search term changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (showAllTags) {
        // Don't update filtered results when showing all tags
        return;
      }

      const results = filterTags(searchTerm);
      setFilteredTags(results);
      setIsDropdownOpen(results.length > 0 && searchTerm.trim().length > 0);
      setSelectedIndex(-1);
    }, 150); // 150ms debounce

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, filterTags, showAllTags]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    // Switch to filtered mode when typing
    if (showAllTags) {
      setShowAllTags(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredTags.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && filteredTags[selectedIndex]) {
          onTagSelect(filteredTags[selectedIndex].value);
          setSearchTerm('');
          setIsDropdownOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        setShowAllTags(false);
        break;
      default:
        break;
    }
  };

  const handleTagClick = (tagValue) => {
    onTagSelect(tagValue);
    setSearchTerm('');
    setIsDropdownOpen(false);
    setShowAllTags(false);
  };

  const handleInputFocus = () => {
    if (!showAllTags && searchTerm.trim() && filteredTags.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleChevronClick = () => {
    if (showAllTags) {
      // Close dropdown if already showing all tags
      setIsDropdownOpen(false);
      setShowAllTags(false);
    } else {
      // Show all tags
      setIsDropdownOpen(true);
      setShowAllTags(true);
      setSelectedIndex(-1);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        setShowAllTags(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get the tags to display in dropdown
  const displayTags = showAllTags ? getAllTags() : filteredTags;

  return (
    <div className={`downloads-tag-search ${className}`} ref={searchRef}>
      <div className="downloads-tag-search-container">
        <i className="fa-solid fa-search downloads-tag-search-icon" aria-hidden></i>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          placeholder={loading ? "Loading tags..." : "Search tags..."}
          className="downloads-tag-search-input"
          disabled={loading}
        />
        <button
          type="button"
          className="downloads-tag-search-chevron"
          onClick={handleChevronClick}
          aria-label="Show all tags"
          title="Show all tags"
          disabled={loading}
        >
          {loading ? (
            <i className="fa-solid fa-spinner fa-spin" aria-hidden></i>
          ) : (
            <i className={`fa-solid fa-chevron-${isDropdownOpen && showAllTags ? 'up' : 'down'}`} aria-hidden></i>
          )}
        </button>
      </div>

      {isDropdownOpen && displayTags.length > 0 && (
        <div className="downloads-tag-search-dropdown">
          {displayTags.map((tag, index) => (
            <button
              key={tag.value}
              type="button"
              className={`downloads-tag-search-result ${index === selectedIndex ? 'is-selected' : ''}`}
              onClick={() => handleTagClick(tag.value)}
            >
              <span className="downloads-tag-search-result-label">{tag.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}